import { ChatThreadTypeEnum, ReportingTermSort, SectionUtil, SimpleTime, TreeSort } from 'common-utils';
import { NotificationIndicatorController } from './controllers/notification-indicator-controller';
import { ActivityFeedNotificationItem } from './models/activity-feed-notification-item';
import { AsgmtSubmissionNotificationItem } from './models/asgmt-submission-notification-item';
import { AsgmtReturnNotificationItem } from './models/asgmt-return-notification-item';
import { AssignmentTemplateNotificationItem } from './models/assignment-template-notification-item';
import { CommunicationNotificationItem } from './models/communication-notification-item';
import { pdsAccessibility } from './pds-accessibility';
import { pdsI18n } from './pds-i18n';
import { pdsLocalStorage } from './pds-local-storage';
import { pdsLogger } from './pds-logger';
import { PdsNaturalSort } from './pds-natural-sort';
import { pdsUtils } from './pds-utils';
import { pdsValidation } from './pds-validation';
import { pdsValidationRulesEngine } from './pds-validation-rules-engine';
import { polymerUtils } from './polymer-utils';
import { GetStreamCacheServiceConfig } from './services/get-stream-cache-service';
import { ActivityFeedNotificationService } from './services/activity-feed-notification-service';
import { AggregatedNotificationService } from './services/aggregated-notification-service';
import { AsgmtSubmissionNotificationService } from './services/asgmt-submission-notification-service';
import { AsgmtReturnNotificationService } from './services/asgmt-return-notification-service';
import { AssignmentTemplateNotificationService } from './services/assignment-template-notification-service';
import { CommunicationNotificationService } from './services/communication-notification-service';
import { ChatThreadService } from './services/chat-thread-service';
import { GetStreamFeedService } from './services/get-stream-feed-service';
import { HelpLinkService } from './services/help-link-service';
import { SimpleParseResolver } from './simple-parse-resolver';
import {PoppableElementFactory} from './poppable-element-factory';
import { MessageHistory } from './message-history';
import { PopoverAccessibilityControllerFactory } from './popover-accessibility-controller-factory';
import { immersiveReaderAccessTokenCache, ImmersiveReaderService } from './services/immersive-reader-service';
import { CancelImmersiveReaderRequest } from './models/cancel-immersive-reader-request';
import { HtmlContentSectionsEngine } from './utils/html-content-sections-engine/html-content-sections-engine';
import { GridWidget, FilterConstraint } from './models/grid-widget/grid-widget';
import { GridWidgetTask } from './models/grid-widget/grid-widget-task';
import { CounterTask } from './models/grid-widget/counter-task';
import { GridWidgetFilter } from './models/grid-widget/grid-widget-filter';
import {TextField} from './models/grid-widget/text-field';
import {TextFilter} from './models/grid-widget/text-filter';
import {DateField} from './models/grid-widget/date-field';
import {MultiField} from './models/grid-widget/multi-field';
import {GridWidgetDataField, DisableFilterOption} from './models/grid-widget/grid-widget-data-field';
import {MultiFieldFilter} from './models/grid-widget/multi-field-filter';
import {SelectField} from './models/grid-widget/select-field';
import {PillSelectField} from './models/grid-widget/pill-select-field';
import {PillSelectFilter} from './models/grid-widget/pill-select-filter';
import {SortConfiguration, SortDirection} from './models/grid-widget/sort-configuration';
import {MultiSelectField} from './models/grid-widget/multi-select-field';
import {MultiSelectFilter} from './models/grid-widget/multi-select-filter';
import {SelectFilter} from './models/grid-widget/select-filter';
import {MultiSelect} from './models/multi-select';
import {OrderedMap} from './models/ordered-map';
import {GridWidgetService} from './services/grid-widget-service';
import { NumberField } from './models/grid-widget/number-field';
import { InverseMultiSelectField } from './models/grid-widget/inverse-multi-select-field';
import { NumberFilter } from './models/grid-widget/number-filter';
import { RestArgument } from './models/grid-widget/rest-argument';
import { FilterContainer } from './models/grid-widget/filter-container';
import { DateFilter } from './models/grid-widget/date-filter';
import { ParseResolver } from './interfaces/parse-resolver';
import { DataField } from './models/data-field';
import { versionAndPaths } from './version-and-paths';
import {serviceInstances} from './services/service-instances';
import { Wizard } from './models/wizard';
import { WizardService } from './services/wizard-service';
import { PdsMessageType, PdsMessageLocation} from './models/message';

declare let global; // this is to explicitly modify global scope in node environments
let globalScope;
if (typeof window !== 'undefined') {
  globalScope = window;
} else if (typeof global !== 'undefined') {
  globalScope = global;
}

if ((<any>globalScope).powerSchoolDesignSystemToolkit == null) {
  let pds = {
    i18n: pdsI18n,
    validation: pdsValidation,
    utils: pdsUtils,
    accessibility: pdsAccessibility,
    localStorage: pdsLocalStorage,
    logger: pdsLogger,
    log: pdsLogger.log,
    naturalSort: new PdsNaturalSort,
    naturalSortConstructor: PdsNaturalSort, //deprecated. use types.PdsNaturalSort instead.
    validationEngine: pdsValidationRulesEngine,
    reportingTermSort: ReportingTermSort, //deprecated. use types.Reporting
    pdsTreeSortUtil: TreeSort, //deprecated. use types.TreeSort
    // I chose to create the accessbility controls as a sepearate instance provided by the factory
    // because we dont want accessibility code shared between polymer and angular, and angular and angular
    factories: {
      PoppableElementFactory,
      PopoverAccessibilityControllerFactory
    },
    //These are all constructors
    controllers: {
      NotificationIndicatorController
    },
    caches: {
      immersiveReaderAccessTokenCache
    },
    //These are all constructors
    services: {
      AggregatedNotificationService,
      ActivityFeedNotificationService,
      AsgmtSubmissionNotificationService,
      AsgmtReturnNotificationService,
      ChatThreadService,
      CommunicationNotificationService,
      GetStreamFeedService,
      GetStreamCacheServiceConfig,
      AssignmentTemplateNotificationService,
      HelpLinkService
    },
    //All new singleton services that should have an instance on the toolkit should go in here.
    //Service Instances are created on the fly to prevent creating them in cases where they are never used.
    serviceInstances: serviceInstances,
    //These are all constructors
    models: {
      ActivityFeedNotificationItem,
      CommunicationNotificationItem,
      AssignmentTemplateNotificationItem,
      AsgmtSubmissionNotificationItem,
      AsgmtReturnNotificationItem,
      CancelImmersiveReaderRequest,
      GridWidget,
      TextField,
      DateField,
      NumberField,
      SelectField,
      SortConfiguration,
      MultiSelectField,
      MultiSelectFilter,
      MultiSelect,
      PillSelectField,
      InverseMultiSelectField,
      OrderedMap,
      GridWidgetTask
    },
    polymerUtils: polymerUtils,
    sectionUtil: SectionUtil,
    preferences: {
      CACHE_KEY_PREFIX: undefined,
      host: '',
      // This is used in pds-search and other components that need s3media
      getS3MediaPath(): string {
        return pds.preferences.host + '/s3media';
      }
    },
    setAutomationChecker: function (automationChecker: any): void {
      if (this.automationChecker == null) {
        this.automationChecker = automationChecker;
        if (pdsLocalStorage.getPersistedBoolean('debug')) {
          this.automationChecker.startChecking();
        }
      } else {
        console.error('The automation checker is already being downloaded in this app. To turn it on run localStorage.setItem(\'debug\', \'true\' and reload the page');
      }
    },
    svgSpritePath: '',
    //Types should generally be things that the user will call new on.
    types: {
      Time: SimpleTime,
      PdsNaturalSort: PdsNaturalSort,
      ParseResolver: SimpleParseResolver,
      ReportingTermSort: ReportingTermSort,
      TreeSort: TreeSort,
      ChatThreadTypeEnum: ChatThreadTypeEnum,
      SortDirection,
      FilterConstraint
    },
    messageHistory: MessageHistory,
    htmlContentSectionsEngine: HtmlContentSectionsEngine
  };

  //No setter on the version. It's a read only variable.
  Object.defineProperty(pds, 'version', {
    get: function(): string {
      return versionAndPaths.version;
    }
  });

  Object.defineProperty(pds, 'cdnAssetPath', {
    get: function(): string {
      return versionAndPaths.cdnAssetPath;
    },
    set: function(value: string): void {
      versionAndPaths.cdnAssetPath = value;
    }
  });

  pds.naturalSort.ignoreCase();

  //give a pointer to the library from each of the sub modules.
  for (let prop in pds) {
    if (prop !== 'polymerUtils' &&
        prop !== 'svgSpritePath' &&
        prop !== 'version' &&
        prop !== 'cdnAssetPath') {
      pds[prop].pds = pds;
    }
  }

  polymerUtils.addPdsObjToPolymerComponent(pds);

  //Add the library to the window so Polymer can read from it.
  (<any>globalScope).powerSchoolDesignSystemToolkit = pds;

}

export default (<any>globalScope).powerSchoolDesignSystemToolkit;

// export any services, models, controllers, factories, and types used in the tool-kit
export {
  AggregatedNotificationService,
  ActivityFeedNotificationService,
  AsgmtSubmissionNotificationService,
  AsgmtReturnNotificationService,
  ChatThreadService,
  CommunicationNotificationService,
  GetStreamFeedService,
  GetStreamCacheServiceConfig,
  AssignmentTemplateNotificationService,
  HelpLinkService,
  ImmersiveReaderService,
  NotificationIndicatorController,
  PoppableElementFactory,
  PopoverAccessibilityControllerFactory,
  ActivityFeedNotificationItem,
  CommunicationNotificationItem,
  AssignmentTemplateNotificationItem,
  AsgmtSubmissionNotificationItem,
  AsgmtReturnNotificationItem,
  SimpleTime,
  PdsNaturalSort,
  SimpleParseResolver,
  ReportingTermSort,
  TreeSort,
  ChatThreadTypeEnum,
  CancelImmersiveReaderRequest,
  HtmlContentSectionsEngine,
  GridWidget,
  SortConfiguration,
  SortDirection,
  MultiSelect,
  OrderedMap,
  RestArgument,
  FilterContainer,
  GridWidgetDataField,
  MultiField,
  MultiFieldFilter,
  TextField,
  TextFilter,
  DateField,
  DateFilter,
  NumberField,
  NumberFilter,
  SelectField,
  SelectFilter,
  MultiSelectField,
  MultiSelectFilter,
  PillSelectField,
  PillSelectFilter,
  FilterConstraint,
  InverseMultiSelectField,
  GridWidgetTask,
  CounterTask,
  DisableFilterOption,
  GridWidgetFilter,
  DataField,
  WizardService,
  Wizard,
  PdsMessageType,
  PdsMessageLocation
};

export { PopperPlacement } from './popper-configuration';

// export interfaces from the tool-kit for TS typing references
export { AppSwitcherItem } from './interfaces/app-switcher-item';
export { PdsError } from './interfaces/pds-error';
export { NavItem } from './interfaces/nav-item';
export { CalendarDay } from './interfaces/calendar-day';
export { NotificationBadge } from './interfaces/notification-badge';
export { ParseResolver } from './interfaces/parse-resolver';
export { PdsImage } from './interfaces/pds-image';
export { ClassPickerNavItem } from './interfaces/class-picker-nav-item';
export { ClassPickerClass } from './interfaces/class-picker-class';
export { ClassSchedule } from './interfaces/class-schedule';
export { StudentSearchResult } from './interfaces/search-result';
export { SearchFavorites } from './interfaces/search-favorites';
export { UnifiedClassroomContext } from './interfaces/unified-classroom-context';
export { Validator, ValidationResult, ValidationRules, BasicValidationRules, SectionStreamActivityUpdate, StreamActivityUpdate, Time, ChatMessageStreamActivityUpdate } from 'common-utils/interfaces';
export { PdsNotificationEvent } from './interfaces/pds-notification-event';
export { AssignmentTemplateNotificationServiceConfig } from './interfaces/assignment-template-notification-service-config';
export { PopoverAccessibilityControllerIf } from './interfaces/popover-accessibility-controller-if';
export { CheckboxOption } from './interfaces/checkbox-option';
export { PdsCheckboxWidget } from './interfaces/pds-checkbox-widget';
export { SelectOption } from './interfaces/select-option';
export { PdsSelectWidget } from './interfaces/pds-select-widget';
export { RadioButtonOption } from './interfaces/radio-button-option';
export { PdsRadioButtonWidget } from './interfaces/pds-radio-button-widget';
export { ImmersiveReaderConfig } from './interfaces/immersive-reader-config';
export { ImmersiveReaderValues } from './interfaces/immersive-reader-values';
export { SelectOptionGroup } from './interfaces/select-option-group';
export { DownloadFileParams, DownloadFileMediaType, DownloadFileCharset } from './interfaces/download-file-params';
export { LinkItem } from './interfaces/link-item';
export { HtmlContentHeuristic } from './interfaces/html-content-heuristic';
export { HtmlContentProperty, HtmlContentValue } from './interfaces/html-content-property';
export { HtmlContentTarget } from './interfaces/html-content-target';
export { ElementConfiguration } from './interfaces/element-configuration';
export { PdsParsedHtmlContent } from './pds-parsed-html-content';
export { AjaxStatus } from './interfaces/AjaxStatus';
export { Counter } from './interfaces/counter1';
export { MultiSelectItem } from './interfaces/multi-select-item';
export { WizardStep } from './interfaces/wizard-step';
